import { escapeRegExp } from './string-utils'

/**
 * Calculates a relevance score for a tag based on how well it matches a search value.
 *
 * Scoring algorithm:
 * - 100: Exact match (case-insensitive)
 * - 40+: Full word match bonus
 * - 20+: Beginning of word match bonus
 * - 0-99: Partial matches based on position and length
 * - 0: No match
 *
 * @param tag - The tag string to score (defaults to empty string)
 * @param matchValue - The search value to match against (defaults to empty string)
 * @returns Score from 0-100 indicating match relevance
 *
 * @example
 * ```typescript
 * tagScore('rock music', 'rock') // returns 100 (exact match)
 * tagScore('rock music', 'music') // returns ~60 (word match)
 * tagScore('classical', 'class') // returns ~45 (beginning match)
 * tagScore('jazz', 'pop') // returns 0 (no match)
 * ```
 */
export function tagScore(tag: string = '', matchValue: string = ''): number {
  if (tag.trim().length === 0 || matchValue.trim().length === 0) return 0
  const lTag = tag.trim().toLowerCase()
  const lMatchValue = matchValue.trim().toLowerCase()

  // 100% match
  if (lTag === lMatchValue) return 100

  const matchPosition = lTag.indexOf(lMatchValue)
  // no match
  if (matchPosition < 0) return 0

  // not exact matches have penalty and can't reach 100 score
  let score = Math.round(
    ((lTag.length - matchPosition) / lTag.length) *
      (40 * (lMatchValue.length / lTag.length)), // prefer shorter strings
  )

  const safeMatchValue = escapeRegExp(matchValue)
  const fullWordMatchResult = lTag.match(`\\b${safeMatchValue}\\b`)
  if (fullWordMatchResult && fullWordMatchResult.length > 0) {
    score += 40 // full word matches get a boost
  } else {
    const beginningWordMatchResult = lTag.match(`\\b${safeMatchValue}`)
    if (beginningWordMatchResult && beginningWordMatchResult.length > 0)
      score += 20 // beggining of the word matches too
  }

  return score > 0 ? score : 0
}

/**
 * Checks if a tag contains a search value (case-insensitive).
 *
 * Performs a simple substring match to determine if the search value
 * appears anywhere within the tag text.
 *
 * @param tag - The tag string to search in
 * @param searchValue - The search value to look for
 * @returns True if the tag contains the search value, false otherwise
 *
 * @example
 * ```typescript
 * matchTag('Rock Music', 'rock') // returns true
 * matchTag('Jazz', 'pop') // returns false
 * matchTag('Electronic Dance', 'dance') // returns true
 * ```
 */
export function matchTag(tag: string, searchValue: string): boolean {
  return tag.toLowerCase().trim().indexOf(searchValue.toLowerCase().trim()) > -1
}
